import { useState, useEffect } from 'react';
import { Loader2, Check, FileText, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { matchingAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';

/**
 * Human write-off of a partially_matched invoice's remaining gap (partial-
 * payment slice, e.g. a bank-fee-shaved shortfall) -- audit-grade, never
 * automatic. The residual amount shown is server-computed (invoice total
 * minus confirmed allocations) at the time the dialog opens; the backend
 * re-validates it against the CURRENT figure on submit and refuses a stale
 * amount (services/invoice_allocations.py::build_accept_residual_update).
 * Accepting sets BOTH matching_status -> matched and payment_status -> paid.
 */
export function AcceptResidualDialog({ invoice, open, onClose, onApplied }) {
  var [reason, setReason] = useState('');
  var [applying, setApplying] = useState(false);

  useEffect(function () {
    if (open) { setReason(''); setApplying(false); }
  }, [open, invoice]);

  if (!invoice) return null;

  var allocated = Number(invoice.amount_paid || 0);
  var total = Number(invoice.amount || 0);
  var residual = Math.max(0, total - allocated);
  var trimmed = reason.trim();

  var handleApply = function () {
    if (!trimmed) return;
    setApplying(true);
    matchingAPI.acceptResidual(invoice.id, residual.toFixed(2), trimmed)
      .then(function () {
        toast.success('Residual accepted — invoice marked matched and paid');
        setApplying(false);
        onApplied && onApplied();
      })
      .catch(function (err) {
        setApplying(false);
        toast.error(getErrorMessage(err));
      });
  };

  return (
    <Dialog open={open} onOpenChange={function (o) { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="accept-residual-dialog">
        <DialogHeader>
          <DialogTitle>Accept residual</DialogTitle>
          <DialogDescription>
            Write off the remaining gap between what was received and the invoice total.
            The invoice will be marked matched and paid; the amount actually received stays
            recorded separately. For a material write-off, use a Pennylane credit note instead
            — this action is for small shortfalls only (e.g. a transfer fee shaved in transit).
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border p-3 bg-muted/40 text-sm space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 font-medium">
              <FileText className="h-4 w-4 text-primary" /> {invoice.invoice_number}
            </span>
            <span className="font-mono">{formatCurrency(total, invoice.currency)}</span>
          </div>
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Allocated so far</span>
            <span className="font-mono" data-testid="accept-residual-allocated">
              {formatCurrency(allocated, invoice.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between font-medium">
            <span>Residual to accept</span>
            <span className="font-mono" data-testid="accept-residual-amount">
              {formatCurrency(residual, invoice.currency)}
            </span>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Reason</label>
          <Textarea
            value={reason}
            onChange={function (e) { setReason(e.target.value); }}
            placeholder="e.g. Wise transfer fee shaved in transit, customer paid in full"
            rows={3}
            data-testid="accept-residual-reason"
          />
          {!trimmed && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <AlertCircle className="h-3 w-3" /> A reason is required to accept a residual.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button onClick={handleApply} disabled={!trimmed || applying} data-testid="accept-residual-apply">
            {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            Accept residual
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
